import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useState } from "react";
import axios from "axios";

// Public Pages
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Contact from "@/pages/Contact";
import RateCalculatorPage from "@/pages/RateCalculatorPage";

// Auth Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";

// Portal Pages
import Dashboard from "@/pages/Dashboard";
import ImportForm from "@/pages/ImportForm";
import ExportForm from "@/pages/ExportForm";
import RateRequestForm from "@/pages/RateRequestForm";
import MyShipments from "@/pages/MyShipments";
import AdminPanel from "@/pages/AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Page view tracker component
function PageTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page visit (silent - no error handling needed)
    const trackVisit = async () => {
      try {
        await axios.post(`${BACKEND_URL}/api/track-visit`, null, {
          params: { page: location.pathname, referrer: document.referrer || null }
        });
      } catch (e) {
        // Silent fail - visitor tracking is non-critical
      }
    };
    trackVisit();
  }, [location.pathname]);
  
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  // Redirect admin users away from customer portal routes
  const ProtectedRoute = ({ children }) => {
    if (loading) return <div>Loading...</div>;
    if (!user) return <Navigate to="/login" />;
    // Admin users should be redirected to admin panel, not customer portal
    if (user.is_admin) return <Navigate to="/admin" />;
    return children;
  };

  const AdminRoute = ({ children }) => {
    if (loading) return <div>Loading...</div>;
    if (!user) return <Navigate to="/login" />;
    if (!user.is_admin) return <Navigate to="/dashboard" />;
    return children;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <PageTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/rate-calculator" element={<RateCalculatorPage />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/register" element={<Register setUser={setUser} />} />
          
          {/* Protected Portal Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard user={user} /></ProtectedRoute>} />
          <Route path="/import" element={<ProtectedRoute><ImportForm user={user} /></ProtectedRoute>} />
          <Route path="/export" element={<ProtectedRoute><ExportForm user={user} /></ProtectedRoute>} />
          <Route path="/rate-request" element={<ProtectedRoute><RateRequestForm user={user} /></ProtectedRoute>} />
          <Route path="/my-shipments" element={<ProtectedRoute><MyShipments user={user} /></ProtectedRoute>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminPanel user={user} /></AdminRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;